/**
 * Sprint 62.0.3 — Non-Vietnam Regulatory Model Assumptions
 *
 * The filing model shipped before this sprint was Vietnam-shaped: one authority
 * per framework which accepts filings, one clock per incident derived from
 * discovery at one fixed length, and no way to say "the thing that actually
 * governs is not the thing you are looking at".
 *
 * This module holds the framework-neutral vocabulary that sits *underneath* the
 * VN flow. `DossierFilingService.submitToAuthority` and
 * `IncidentResponseMaturityService.submitToAuthority` keep their signatures and
 * behaviour, and become thin callers supplying the VN preset below — the VN
 * evidence-pack flow and the Forseti advisory services that resolve filing
 * lineage through `submission-capability-registry.ts` are untouched. (Both were
 * named after one receiving authority until the residue run of
 * fix-sprint-20260905-2135-followup-2 renamed them under R-46.)
 *
 * Every type here is additive and optional at its use sites, so all 102 existing
 * frameworks and every existing record remain valid without migration (AC10).
 */
// ── Submission channels (AC1) ────────────────────────────────────
/**
 * The three channels Decree 356 filings use. Unchanged, still valid everywhere,
 * and still the only values the VN filing path accepts at runtime.
 */
export const VN_SUBMISSION_CHANNELS = ['mps_portal', 'in_person', 'postal'];
export function isVnSubmissionChannel(value) {
    return typeof value === 'string' && VN_SUBMISSION_CHANNELS.includes(value);
}
// ── Regulatory clocks (AC3–AC6) ──────────────────────────────────
/**
 * What a clock counts from. Explicit because the distinction is one regulators
 * actively test: GDPR Article 33 runs from *awareness*, while the pre-62.0.3
 * implementation ran every clock from *discovery*.
 */
/**
 * ## Sprint 63.8.0, D8 — this is the INCIDENT selector's vocabulary, not the corpus's
 *
 * These six members are not, and have never been, the set of values a pack may
 * author. Measured 2026-09-21 over `packs/**`: **36 distinct `trigger_event`
 * values across 17 `deadlines.json` files, 32 of them outside this list** — the
 * September banking, data-services and cybersecurity intakes each added
 * vocabulary of their own.
 *
 * The list is NOT extended to cover them, and that is a ruling rather than an
 * omission. Sprint 62.19.0 settled it: **licence vocabulary is not added to the
 * incident union.** A sector-licence lifecycle event is not an incident trigger
 * and giving both one vocabulary would make the type unable to say which kind of
 * thing it holds. The governing rule is stated once in REUSE.md §*Selectors the
 * sector-licensing module reads* — *"The selector is `event_type`; the vocabulary
 * is the pack's"* — and `licence-service.ts:846` is the shipped precedent for
 * reading a pack-authored trigger against a selector's own union.
 *
 * What changed in 63.8.0 is that the DECLARATION stopped claiming otherwise.
 * `RegulatoryDeadline.trigger_event` now admits what packs actually write, and
 * anything narrowing to a member of this list goes through `incidentTriggerOf`.
 */
export const DEADLINE_TRIGGER_EVENTS = [
    'awareness',
    'discovery',
    'containment',
    'authority_request',
    'assessment_complete',
    'framework_specific',
];
/**
 * Narrow a pack-authored trigger to the incident selector's vocabulary, or
 * `null`.
 *
 * Modelled on `licence-service.ts:846`'s `triggerOf`, which tests membership of
 * `LICENCE_TRIGGER_EVENTS` and answers `undated_reason: 'unrecognised_trigger_event'`
 * rather than casting. A cast — which is what `regulatory-clock-resolver.ts:295`
 * did — makes the compiler agree to a claim nothing checked.
 *
 * Returns `null` for the 32 measured corpus values that belong to another
 * selector. `null` is not an error: it means *this trigger is not an incident
 * trigger*, which for most of those values is simply true.
 */
export function incidentTriggerOf(value) {
    return typeof value === 'string' && DEADLINE_TRIGGER_EVENTS.includes(value)
        ? value
        : null;
}
/**
 * A regulatory period, expressed the way the instrument expresses it.
 *
 * Kept faithful rather than normalised to a single unit: GDPR Article 33 states
 * *72 hours* and the NIS2 early warning states *24 hours*, while a filing window
 * stated in working days cannot be converted to hours without knowing the
 * calendar. Exactly one of `hours` or `days` is set.
 */
/**
 * What happens to the organisation if a clock runs out — Sprint 65.8.0 (AC13).
 *
 * ## Why a due date is not enough
 *
 * The platform has had one vocabulary for every deadline it shows, and there
 * are two kinds. Verification against primary sources found both sitting on the
 * same record:
 *
 * - **`sanction_exposure`** — the ordinary kind. Missing it exposes the
 *   organisation to a penalty, and the duty survives the miss: it is still owed,
 *   late.
 * - **`right_extinguished`** — a window to *act*, where the organisation is the
 *   one exercising a right rather than discharging a duty. Missing it does not
 *   expose anybody to anything. It **destroys the right**, permanently, and
 *   there is no late version of it.
 *
 * Showing those two the same way is a product defect, not a nicety. An operator
 * triaging a list of dates by penalty size will rationally deprioritise the
 * second kind — and the second kind is the one that cannot be recovered.
 *
 * ## The two can run at once, and in tension
 *
 * The case that motivated this: a challenge window and a compliance clock run
 * **concurrently** on the same administrative decision, because lodging a
 * challenge does not suspend the duty to comply with the decision being
 * challenged. An operator who reads one as pausing the other is wrong in a way
 * that costs money. A surface showing both must therefore show what each one
 * *is*, not merely when each falls due.
 *
 * ## Why it is on the CLOCK and not inferred
 *
 * It is a property of the instrument, so it is read from the installed record
 * and never derived. Nothing about a period's length, unit or trigger tells you
 * which kind it is, and a platform that guessed would be asserting a legal
 * consequence nobody wrote down.
 */
export const CLOCK_LAPSE_CONSEQUENCES = ['sanction_exposure', 'right_extinguished'];
//# sourceMappingURL=regulatory-filing.js.map